import { Box } from "@mui/material";
import { Dayjs } from "dayjs";
import { useEffect, useState } from "react";
import { useQuery } from "react-query";
import AccessDenied from "../../../components/access_denied/access_denied";
import CmsLayout from "../../../layouts/cms/cms_layout";
import MainLayout from "../../../layouts/main/main_layout";
import { useFeedback } from "../../../providers/feedback/feeedback";
import { queryClient } from "../../../providers/query/query";
import useUser from "../../../providers/user/user_provider";
import { deleteAppPolling, getAppPollingList } from "../../../services/cms/cms";
import DeleteCmsItemDialog from "../components/delete_cms_item/delete_cms_item";
import FilterAppPolling, { AppPollingFilterValues } from "../components/filter_app_polling/filter_app_polling";
import AppPollingListing, { StatusFilter } from "../components/app_polling_listing/app_polling_listing";
import SortAppPollingPopup, {
  DEFAULT_APP_POLLING_SORT_OPTION,
  AppPollingSortOption,
  appPollingSortOptionLabel,
  appPollingSortOptionToParams,
} from "../components/sort_app_polling_popup/sort_app_polling_popup";
import { AppPolling } from "../models/cms";
import { useSelectedOrgStore } from "../../../store/use-selected-org-store";

const ROWS_PER_PAGE = 10;

export default function AppPollingPage() {
  const { hasPermission } = useUser();
  const { showSnackbar } = useFeedback();
  const [page, setPage] = useState(1);
  const [searchValue, setSearchValue] = useState("");
  const [debouncedSearchValue, setDebouncedSearchValue] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [dateFrom, setDateFrom] = useState<Dayjs | null>(null);
  const [dateTo, setDateTo] = useState<Dayjs | null>(null);
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [sortOption, setSortOption] = useState<AppPollingSortOption>(DEFAULT_APP_POLLING_SORT_OPTION);
  const [sortDialogOpen, setSortDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<AppPolling | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const { selectedOrgId } = useSelectedOrgStore();

  useEffect(() => {
    const handler = setTimeout(() => setDebouncedSearchValue(searchValue), 500);
    return () => clearTimeout(handler);
  }, [searchValue]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearchValue, statusFilter, dateFrom, dateTo, selectedOrgId, sortOption]);

  const fromDate = dateFrom?.format("YYYY-MM-DD");
  const toDate = dateTo?.format("YYYY-MM-DD");

  const { data, isLoading, isFetching } = useQuery(
    ["cms-app-polling", page, debouncedSearchValue, statusFilter, selectedOrgId, sortOption, fromDate, toDate],
    async () => {
      const { sortBy, sortOrder } = appPollingSortOptionToParams(sortOption);
      const response = await getAppPollingList({
        page,
        size: ROWS_PER_PAGE,
        search: debouncedSearchValue,
        status: statusFilter === "all" ? undefined : statusFilter === "active",
        orgId: selectedOrgId || undefined,
        sortBy,
        sortOrder,
        fromDate,
        toDate,
      });
      return response?.data?.data;
    },
    { keepPreviousData: true }
  );

  const list: AppPolling[] = data?.list || [];
  const count = data?.total || 0;

  const filterCount = [statusFilter !== "all", !!dateFrom && !!dateTo].filter(Boolean).length;

  const handleApplyFilters = (values: AppPollingFilterValues) => {
    setStatusFilter(values.statusFilter);
    setDateFrom(values.dateFrom);
    setDateTo(values.dateTo);
  };

  const handleDeleteConfirm = () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    deleteAppPolling(deleteTarget.id)
      .then(() => {
        showSnackbar("Poll deleted successfully");
        queryClient.invalidateQueries({ queryKey: ["cms-app-polling"] });
      })
      .catch((e) => {
        showSnackbar(e?.response?.data?.error?.message || "Something went wrong");
      })
      .finally(() => {
        setIsDeleting(false);
        setDeleteTarget(null);
      });
  };

  return (
    <MainLayout>
      {hasPermission("cms", "read") ? (
        <CmsLayout>
          <AppPollingListing
            list={list}
            count={count}
            page={page}
            setPage={setPage}
            isLoading={isLoading || isFetching}
            searchValue={searchValue}
            setSearchValue={setSearchValue}
            filterCount={filterCount}
            onOpenFilters={() => setFilterDialogOpen(true)}
            sortLabel={appPollingSortOptionLabel(sortOption)}
            onOpenSort={() => setSortDialogOpen(true)}
            onDelete={setDeleteTarget}
          />
          <DeleteCmsItemDialog
            open={!!deleteTarget}
            itemName={deleteTarget?.question}
            isDeleting={isDeleting}
            onClose={() => setDeleteTarget(null)}
            onConfirm={handleDeleteConfirm}
          />
          {filterDialogOpen && (
            <FilterAppPolling
              open={filterDialogOpen}
              onClose={() => setFilterDialogOpen(false)}
              statusFilter={statusFilter}
              dateFrom={dateFrom}
              dateTo={dateTo}
              onApply={handleApplyFilters}
            />
          )}
          <SortAppPollingPopup
            open={sortDialogOpen}
            onClose={() => setSortDialogOpen(false)}
            sortOption={sortOption}
            setSortOption={setSortOption}
          />
        </CmsLayout>
      ) : (
        <Box display="flex" flex={1} width="100%" height="100%">
          <AccessDenied />
        </Box>
      )}
    </MainLayout>
  );
}
